import React, { useEffect, useState } from "react";
import {
    Autocomplete,
    TextField,
    Typography,
    Box,
    CircularProgress
} from "@mui/material";

import AxiosInstance from "../../services/AxiosInstance";
import {
    TICKET_URL
} from "../../Constants/Constants";

const TicketAutocompleteField = ({
    field,
    value,
    onChange,
    selectedTicket = null
}) => {

    const [ticketOptions, setTicketOptions] =
        useState(
            selectedTicket
                ? [selectedTicket]
                : []
        );

    const [loading, setLoading] =
        useState(false);

    const [inputValue, setInputValue] =
        useState(
            selectedTicket?.ticketId || ""
        );

    /*
     * When Asset data is loaded,
     * add the existing ticket to options.
     */
    useEffect(() => {

        if (!selectedTicket?.ticketId) {
            return;
        }

        setTicketOptions(prev => {

            const exists = prev.some(
                item =>
                    String(item.ticketId) ===
                    String(selectedTicket.ticketId)
            );

            if (exists) {
                return prev;
            }

            return [
                selectedTicket,
                ...prev
            ];
        });

        setInputValue(
            selectedTicket.ticketId
        );

    }, [selectedTicket]);


    /*
     * Find currently selected ticket.
     *
     * value = incidentTicketId
     */
    const selectedOption =
        ticketOptions.find(
            option =>
                String(option.ticketId) ===
                String(value)
        ) ||
        (
            selectedTicket &&
            String(selectedTicket.ticketId) ===
            String(value)
                ? selectedTicket
                : null
        );


    /*
     * Search tickets
     */
    const searchTickets = async (
        searchValue
    ) => {

        const search =
            searchValue?.trim();

        if (!search || search.length < 2) {

            /*
             * Don't remove selected ticket
             * while clearing/searching.
             */
            setTicketOptions(
                selectedTicket
                    ? [selectedTicket]
                    : []
            );

            return;
        }

        try {

            setLoading(true);

            const response =
                await AxiosInstance.get(
                    `${TICKET_URL}/search`,
                    {
                        params: {
                            ticketId: search
                        }
                    }
                );

            const results =
                response.data || [];

            /*
             * Keep existing selected ticket
             * even when search returns
             * something else.
             */
            if (selectedTicket) {

                const exists =
                    results.some(
                        item =>
                            String(
                                item.ticketId
                            ) ===
                            String(
                                selectedTicket.ticketId
                            )
                    );

                setTicketOptions(
                    exists
                        ? results
                        : [
                            selectedTicket,
                            ...results
                        ]
                );

            } else {

                setTicketOptions(results);
            }

        } catch (error) {

            console.error(
                "Unable to search tickets",
                error
            );

            setTicketOptions(
                selectedTicket
                    ? [selectedTicket]
                    : []
            );

        } finally {

            setLoading(false);
        }
    };


    return (
        <Autocomplete
            fullWidth

            options={ticketOptions}

            /*
             * IMPORTANT:
             * value must be the ticket object,
             * not just the ticket ID.
             */
            value={selectedOption || null}

            inputValue={inputValue}

            loading={loading}

            isOptionEqualToValue={
                (option, selected) =>
                    String(option.ticketId) ===
                    String(selected.ticketId)
            }

            getOptionLabel={(option) =>
                option?.ticketId || ""
            }

            onInputChange={(
                event,
                newInputValue,
                reason
            ) => {

                setInputValue(
                    newInputValue
                );

                if (reason === "input") {

                    searchTickets(
                        newInputValue
                    );
                }
            }}

            onChange={(
                event,
                newValue
            ) => {

                /*
                 * Store only ticket ID
                 * in lifecycleData.
                 */
                onChange({
                    target: {
                        name: field.name,
                        value:
                            newValue?.ticketId || ""
                    }
                });

                /*
                 * Update displayed text.
                 */
                setInputValue(
                    newValue?.ticketId || ""
                );
            }}

            renderOption={(
                props,
                option
            ) => (

                <li
                    {...props}
                    key={option.ticketId}
                >

                    <Box>

                        <Typography
                            variant="body2"
                            fontWeight={600}
                        >
                            {option.ticketId}
                        </Typography>

                        {option.title && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {option.title}
                            </Typography>
                        )}

                        {option.status && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                            >
                                Status: {option.status}
                            </Typography>
                        )}

                    </Box>

                </li>
            )}

            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    label={field.label}
                    required={field.required}

                    slotProps={{
                        input: {
                            ...params.InputProps,

                            endAdornment: (
                                <>
                                    {loading && (
                                        <CircularProgress
                                            size={20}
                                        />
                                    )}

                                    {
                                        params.InputProps
                                            .endAdornment
                                    }
                                </>
                            )
                        }
                    }}
                />
            )}
        />
    );
};

export default TicketAutocompleteField;